import { IChoiceGroupOption, MessageBarType } from '@fluentui/react';
import {
  Button,
  buttonStylesGhost,
  buttonStylesPrimary,
  ChoiceGroup,
  choiceGroupStylesHorizontalBox,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { FormEvent, useCallback, useState } from 'react';

import { fsApi } from '../../featurestore/api';
import { useDataSourceUpload } from '../../featurestore/api';
import { FeatureStoreService } from '../../featurestore/featurestoreApi';
import { CoreService, StartIngestJobRequest } from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { OnlineIngestionTokenResponse } from '../../featurestore/gen/ai/h2o/featurestore/api/v1/OnlineApi.pb';
import { ContentsTitle, NameValue } from '../../featurestore/models';
import { getErrorMessageBarMessage, hasAPIError } from '../../featurestore/utils';
import { IngestOnline } from '../IngestOnline/IngestOnline';
import { DataSourceInfo, SelectDataSource } from '../SelectDataSource/SelectDataSource';
import { IIngestClassNames, IIngestStyles, ingestStylesDefault } from './Ingest.styles';

enum Labels {
  online = 'Ingest Online',
  offline = 'Ingest Offline',
}

const options: IChoiceGroupOption[] = [
  { key: Labels.offline, text: Labels.offline },
  { key: Labels.online, text: Labels.online },
];

interface IIngestProps {
  featureSetVersion?: string;
  featureSetId?: string;
  onSave?: (isOnline?: boolean) => void;
  onCancel?: () => void;
}

export const Ingest = ({ featureSetVersion, featureSetId, onSave, onCancel }: IIngestProps) => {
  const [dataSourceInfo, setDataSourceInfo] = useState<DataSourceInfo>({});
  const [optionLabel, setOptionLabel] = useState(Labels.offline);
  const [onlineValues, setOnlineValues] = useState<{ jsonString?: string; nameValues?: NameValue[] }>();
  const { uploadDataSourceFiles } = useDataSourceUpload();
  const { addToast } = useToast();
  const onChange = useCallback((_e?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IChoiceGroupOption) => {
    if (option) {
      setOptionLabel(option.key as Labels);
    }
  }, []);

  const onChangeOnline = useCallback((jsonString?: string, nameValues?: NameValue[]) => {
    setOnlineValues({ jsonString, nameValues });
  }, []);
  const onUpdate = useCallback(async (values: StartIngestJobRequest, dataSourceInfo: DataSourceInfo) => {
    try {
      const { tempDataSourceFileInfo, tempTransformationInfo, derivedFrom, ...source } = dataSourceInfo;
      const dataSource = await uploadDataSourceFiles(tempDataSourceFileInfo, source.dataSource);
      if (dataSource && Object.keys(dataSource).length > 0) {
        source.dataSource = dataSource;
      }
      await fsApi(CoreService.StartIngestJob, { ...values, ...source });
      addToast({
        messageBarType: MessageBarType.success,
        message: 'Ingest Job Started Successfully!',
      });
    } catch (err) {
      addToast(getErrorMessageBarMessage(err, `An error has occurred while starting ingest job: `));
    } finally {
      onSave && onSave();
    }
  }, []);
  const onUpdateOnline = useCallback(async () => {
    try {
      const { token, signature }: OnlineIngestionTokenResponse = await fsApi(CoreService.GenerateIngestToken, {
        featureSetId,
        featureSetVersion,
      });
      const { jsonString, nameValues } = onlineValues || {};
      let resultJsonString = jsonString;
      if (nameValues) {
        const json: { [key: string]: string } = {};
        nameValues.forEach(({ name = '', value = '' }: NameValue) => {
          if (name) {
            json[name] = value;
          }
        });
        resultJsonString = JSON.stringify(json);
      }
      const res = await fsApi(FeatureStoreService.OnlineIngestion, {
        token,
        signature,
        rows: [resultJsonString],
        featureSetId,
        featureSetMajorVersion: `${parseInt(featureSetVersion || '0')}`,
      });
      if (hasAPIError(res)) {
        addToast(getErrorMessageBarMessage(res, `An error has occurred while starting online ingestion: `));
      } else {
        addToast({
          messageBarType: MessageBarType.success,
          message: 'Online ingestion is done successfully!',
        });
      }
    } catch (err) {
      addToast(getErrorMessageBarMessage(err, `An error has occurred while starting online ingestion: `));
    } finally {
      onSave && onSave(true);
    }
  }, [onlineValues]);
  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      if (optionLabel === Labels.offline) {
        onUpdate({ featureSetId, featureSetVersion }, dataSourceInfo);
      } else {
        onUpdateOnline();
      }
      e.preventDefault();
    },
    [dataSourceInfo, optionLabel, onUpdateOnline, onUpdate]
  );
  const classNames = useClassNames<IIngestStyles, IIngestClassNames>('Ingest', ingestStylesDefault);
  return (
    <div className={classNames.root} data-test="ingest">
      <form className={classNames.form} onSubmit={onSubmit}>
        <div className={classNames.fields}>
          <ChoiceGroup
            styles={choiceGroupStylesHorizontalBox}
            options={options}
            required
            label="Select a way to ingest"
            key="schema-type"
            defaultSelectedKey={Labels.offline}
            onChange={onChange}
          />
          {optionLabel === Labels.offline ? (
            <SelectDataSource
              hasUploadFile
              onChange={(dataSourceInfo) => {
                setDataSourceInfo(dataSourceInfo);
              }}
            />
          ) : (
            <IngestOnline featureSetId={featureSetId} featureSetVersion={featureSetVersion} onChange={onChangeOnline} />
          )}
        </div>
        <div className={classNames.buttons}>
          <Button styles={buttonStylesGhost} text="Cancel" onClick={onCancel} style={{ marginRight: 8 }} />
          <Button styles={buttonStylesPrimary} type="submit" text={ContentsTitle.ingest} />
        </div>
      </form>
    </div>
  );
};
